import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Convert, PayoutRatio } from "@/models/responses";
import { ColDef } from "ag-grid-community";
import Grid from "@/lib/organisms/Grid";
import { CustomCellRendererProps } from "ag-grid-react";
import { decodeErrorResponse } from "@/utils/errors";
import { errorPopup, successPopup } from "@/utils/alerts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { EditRatioDialog } from "@/lib/shadcn/components/edit-apartment-ratio-dialog";
import { dateToString } from "@/utils/periods";
import { utcDateFilterParams } from "@/utils/grid";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { apiUrl, fetchOptions } from "@/hooks/fetchOptions";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";

interface ExtendedActionsRendererProps extends CustomCellRendererProps {
    onEdit: (id: number) => void;
}

export const ActionsRenderer: React.FC<ExtendedActionsRendererProps> = (params: ExtendedActionsRendererProps) => {
    return (
        <div>
            <button
                type="button"
                aria-label="Edit ratio"
                onClick={function () {
                    return params.onEdit(params.data.id);
                }}
                style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "inherit",
                }}
                className={"text-info-emphasis"}
            >
                <FontAwesomeIcon icon={faEdit} />
            </button>
        </div>
    );
};

const PayoutRatiosPage: React.FC = () => {
    const apartmentEntities = useActiveApartmentEntities();
    console.log(apartmentEntities);
    const [ratios, setRatios] = useState<PayoutRatio[]>([]);
    const [apartmentToEdit, setApartmentToEdit] = useState<PayoutRatio | undefined>(undefined);
    const [showEditRatiosModal, setShowEditRatiosModal] = useState(false);
    const gridName = "Latest Payout Ratios";

    const editPayoutRatio = useCallback(
        (id: number) => {
            const apartmentRatio: PayoutRatio = ratios.find((ratio) => ratio.id === id)!;
            setApartmentToEdit(apartmentRatio);
            setShowEditRatiosModal(true);
        },
        [ratios],
    );

    const { predicates } = useApartmentFilters();

    /**
     * Apply the shared apartment-filter predicates to the ratios list. Same
     * pattern as `managementFeeRatiosPage.tsx` — resolve each row's
     * `apartmentId` via `apartmentEntities`, then `predicates.every(...)`.
     */
    const filteredRatios = useMemo(() => {
        if (predicates.length === 0) {
            return ratios;
        }
        return ratios.filter((ratio) => {
            const apartment = apartmentEntities[ratio.apartmentId];
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
    }, [ratios, predicates, apartmentEntities]);

    const columnDefinitions: ColDef[] = [
        {
            field: "apartmentId",
            headerName: "Apartment",
            valueGetter: (params) => apartmentEntities[params.data.apartmentId]?.name,
            width: 350,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        {
            field: "payoutRatio",
            headerName: "Payout Ratio",
            valueFormatter: (params) => `${(params.value * 100).toFixed(0)}%`,
            width: 200,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => value.data.payoutRatio * 100,
        },
        {
            field: "fromDate",
            headerName: "Effective From",
            cellDataType: "date",
            filter: "agDateColumnFilter",
            filterParams: utcDateFilterParams,
            width: 250,
        },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: ActionsRenderer,
            cellRendererParams: {
                onEdit: (apartmentId: number) => editPayoutRatio(apartmentId),
            },
            filter: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        fetch(`${apiUrl}/api/admin/apartments/payout-ratios`, fetchOptions)
            .then((response) => response.text())
            .then((data) => {
                let ratios = Convert.toGetPayoutRatiosResponse(data).payoutRatios;
                setRatios(ratios);
            })
            .catch((error) => {
                toast({
                    title: "Error",
                    description: `Failed to fetch payout ratios: ${error.message}`,
                    variant: "destructive",
                });
            });
    }, []);

    const handleModalSubmitted = useCallback(
        (apartmentToEdit: PayoutRatio, payoutRatio: number, month: number, year: number, existingRatio?: PayoutRatio) => {
            const apartmentId = apartmentToEdit?.apartmentId;
            const fromDate = new Date(year, month, 1);
            const isUpdate = !!existingRatio;

            const url = isUpdate
                ? `${apiUrl}/api/admin/apartments/${apartmentId}/payout-ratios/${existingRatio.id}`
                : `${apiUrl}/api/admin/apartments/payout-ratios`;
            const method = isUpdate ? "PUT" : "POST";

            fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    apartmentId: apartmentId,
                    payoutRatio: payoutRatio,
                    fromDate: dateToString(fromDate),
                }),
            })
                .then((response) => {
                    if (!response.ok) return decodeErrorResponse(response);
                    return response.text();
                })
                .then((data) => {
                    let updatedPayoutRatio: PayoutRatio = Convert.toGetPayoutRatioResponse(data).payoutRatio;
                    let updatedRatios: PayoutRatio[];

                    if (isUpdate) {
                        // Update existing ratio
                        updatedRatios = ratios.map((ratio: PayoutRatio) =>
                            ratio.id === updatedPayoutRatio.id ? updatedPayoutRatio : ratio,
                        );
                    } else {
                        // Add new ratio and remove old one for same apartment (if this is the latest)
                        updatedRatios = ratios.filter((ratio: PayoutRatio) => ratio.apartmentId !== apartmentId);
                        updatedRatios.push(updatedPayoutRatio);
                    }

                    setRatios(updatedRatios);
                    successPopup(isUpdate ? "Payout ratio updated successfully" : "Payout ratio created successfully");
                    setShowEditRatiosModal(false);
                })
                .catch((error) => {
                    void errorPopup("Failed to update ratio:" + error.message);
                });
        },
        [ratios],
    );

    const handleModalClosed = useCallback(() => {
        setApartmentToEdit(undefined);
        setShowEditRatiosModal(false);
    }, []);

    return (
        <div data-testid="payout-ratios-page" className="flex flex-col gap-2">
            <ApartmentFilterBar />
            <EditRatioDialog
                open={showEditRatiosModal}
                onClose={handleModalClosed}
                onSubmit={(payoutRatio, month, year, existingRatio) =>
                    handleModalSubmitted(apartmentToEdit!, payoutRatio, month, year, existingRatio as PayoutRatio)
                }
                modalTitle={"Payout Ratio"}
                apartmentName={apartmentEntities[apartmentToEdit?.apartmentId || 0]?.name || ""}
                apartmentId={apartmentToEdit?.apartmentId || 0}
                ratio={apartmentToEdit?.payoutRatio || 0}
                fromDate={apartmentToEdit?.fromDate || new Date()}
            />
            <Grid<PayoutRatio>
                columnDefs={columnDefinitions}
                gridName={gridName}
                initialRowData={filteredRatios}
                canExport={false}
                getRowId={(params) => params.data.apartmentId.toString()}
            />
        </div>
    );
};
export default PayoutRatiosPage;
